import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Skeleton } from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import YouTube from 'react-youtube';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
// import { useTheme } from '@mui/material/styles';
import { useDevice } from '../hooks/useResponsive';

FeaturedCars.propTypes = {
  youtubeVideosList: PropTypes.any,
  productSingle: PropTypes.string,
};

export default function FeaturedCars({ youtubeVideosList, productSingle }) {
  const { isMobile } = useDevice();
  // const theme = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [invalidIds, setInvalidIds] = useState({});
  const [loadingVideos, setLoadingVideos] = useState({}); // 🔥 track loading state
  const [isPlayingMap, setIsPlayingMap] = useState({});
  const playersRef = React.useRef({});
  const sliderRef = React.useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  // console.log('youtubeVideosList', youtubeVideosList);
  useEffect(() => {
    const validateVideos = async () => {
      if (!youtubeVideosList?.length) return;

      const checks = await Promise.all(
        youtubeVideosList.map(async (video) => {
          const id = video?.video_id;
          if (!id) return { id, invalid: true }; // no ID = invalid
          const valid = await isYouTubeVideoValid(id);
          return { id, invalid: !valid };
        })
      );

      const result = checks.reduce((acc, { id, invalid }) => {
        if (id && invalid) acc[id] = true;
        return acc;
      }, {});

      setInvalidIds(result);
    };

    validateVideos();
  }, [youtubeVideosList]);

  const isYouTubeVideoValid = async (videoId) => {
    const url = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
    try {
      const res = await fetch(url);
      return res.ok;
    } catch {
      return false;
    }
  };

  const CustomPrevArrow = ({ onClick, currentSlide }) => {
    if (currentSlide === 0) return null;
    return (
      <Stack
        role="button"
        tabIndex="0"
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
        sx={{
          backgroundColor: 'rgba(100, 100, 100, 0.9)',
          borderRadius: '50%',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 30, color: 'white' }} />
      </Stack>
    );
  };
  CustomPrevArrow.propTypes = {
    onClick: PropTypes.func,
    currentSlide: PropTypes.number.isRequired,
  };

  const CustomNextArrow = ({ onClick, currentSlide, slideCount, slidesToShow }) => {
    if (currentSlide >= slideCount - slidesToShow) return null;
    return (
      <Stack
        role="button"
        tabIndex="0"
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
        sx={{
          backgroundColor: 'rgba(100, 100, 100, 0.9)',
          borderRadius: '50%',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
        }}
      >
        <ChevronRightIcon sx={{ fontSize: 30, color: 'white' }} />
      </Stack>
    );
  };
  CustomNextArrow.propTypes = {
    onClick: PropTypes.func,
    currentSlide: PropTypes.number.isRequired,
    slideCount: PropTypes.number.isRequired,
    slidesToShow: PropTypes.number.isRequired,
  };

  const STS = isMobile ? 1.1 : 3.5;
  const slideCount = youtubeVideosList?.length;
  const carouselSettings = {
    dots: true,
    infinite: false,
    autoplay: false,
    speed: 500,
    slidesToShow: STS,
    slidesToScroll: 1,
    arrows: true,
    draggable: true, // Enable dragging
    swipeToSlide: true, // Enable swipe to slide
    touchMove: true, // Enable touch movement
    prevArrow: <CustomPrevArrow currentSlide={currentSlide} />,
    nextArrow: <CustomNextArrow currentSlide={currentSlide} slideCount={slideCount} slidesToShow={STS} />,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    appendDots: (dots) => (
      <div style={{ textAlign: 'center', position: 'relative', bottom: '-11px' }}>
        <ul style={{ margin: 0, padding: 0, display: 'inline-flex' }}>{dots}</ul>
      </div>
    ),
    customPaging: (i) => {
      const slidesToScroll = carouselSettings.slidesToScroll || 1;
      const isActive = i === Math.round(currentSlide / slidesToScroll);
      return (
        <div
          style={{
            width: isActive ? '25px' : '12px',
            height: isActive ? '5px' : '5px',
            borderRadius: '8px',
            backgroundColor: isActive ? '#FFFFFF' : '#555555',
            margin: '0 2px',
            transition: 'all 0.3s ease',
          }}
        />
      );
    },
  };

  const opts = {
    width: isMobile ? '310px' : '580px',
    height: isMobile ? '300px' : '320px',
    host: 'https://www.youtube-nocookie.com',
    playerVars: {
      autoplay: 0,
      rel: 0, // Hide related videos from other channels (may still show same-channel)
      modestbranding: 1, // Reduce YouTube branding
      controls: 0, // Hide all player controls/icons
      fs: 0, // Hide fullscreen button
      iv_load_policy: 3, // Hide video annotations
      cc_load_policy: 0, // Disable closed captions by default
      disablekb: 1, // Disable keyboard controls
      playsinline: 1, // Play inline on iOS
    },
  };

  const handleCustomPlay = (videoId) => {
    // Pause other videos
    Object.entries(playersRef.current).forEach(([id, player]) => {
      if (id !== String(videoId) && player?.pauseVideo) {
        try {
          player.pauseVideo();
        } catch (_) {
          /* noop */
        }
      }
    });

    // Play requested video
    const player = playersRef.current[String(videoId)];
    if (player?.playVideo) {
      try {
        player.playVideo();
      } catch (_) {
        /* noop */
      }
    }
  };

  const handleDragStart = useCallback((e) => {
    if (!sliderRef.current) return;

    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX, y: clientY });
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging || !sliderRef.current) return;

      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - dragStart.x;
      const threshold = 50; // Minimum drag distance

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          // Drag right - go to previous slide
          sliderRef.current.slickPrev();
        } else {
          // Drag left - go to next slide
          sliderRef.current.slickNext();
        }
        setIsDragging(false);
      }
    },
    [isDragging, dragStart]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse and touch events for drag handling
  useEffect(() => {
    const handleMouseMove = (e) => handleDragMove(e);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e) => handleDragMove(e);
    const handleTouchEnd = () => handleDragEnd();

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  return (
    <div className="youtube-carousal">
      <Stack sx={{ pb: 2, pl: { xs: 2, md: '75px', lg: '75px' }, mb: productSingle && !isMobile ? 0 : 5 }}>
        <Slider ref={sliderRef} {...carouselSettings}>
          {youtubeVideosList?.map((video, index) => {
            const isInvalid = invalidIds[video?.video_id];
            return (
              <Box
                key={index}
                className={`react-player react-youtube-player react-youtube-player-home yt-player-${index}`}
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  pr: isMobile ? 1 : 3,
                  color: '#fff',
                  transition: 'filter 0.3s ease, opacity 0.3s ease',
                  position: 'relative',
                }}
              >
                {!isInvalid ? (
                  <>
                    {/* Drag Handle - Only show when video is NOT playing */}
                    {!isPlayingMap[video?.video_id] && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '0px',
                          right: '0px',
                          top: 0,
                          height: '100%',
                          zIndex: 2,
                          cursor: 'grab',
                          '&:active': {
                            cursor: 'grabbing',
                          },
                          '&:hover': {
                            // backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            '&::after': {
                              content: '"⋯"',
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              opacity: 0.7,
                            },
                          },
                        }}
                        onMouseDown={handleDragStart}
                        onTouchStart={handleDragStart}
                      />
                    )}

                    <YouTube
                      videoId={video?.video_id}
                      opts={opts}
                      className="react-player-video my-custom-youtube-box"
                      iframeClassName={isMobile ? 'w-90' : ''}
                      onReady={(e) => {
                        playersRef.current[String(video?.video_id)] = e?.target;
                        setLoadingVideos((prev) => ({ ...prev, [video?.video_id]: false }));
                      }}
                      onStateChange={(e) => {
                        const state = e?.data;
                        const isPlaying = state === 1;
                        setIsPlayingMap((prev) => ({ ...prev, [video?.video_id]: isPlaying }));
                      }}
                      onPlay={() => {
                        document.querySelectorAll('.react-youtube-player iframe').forEach((iframe) => {
                          if (iframe !== document.querySelector(`.yt-player-${index} iframe`)) {
                            iframe.contentWindow.postMessage(
                              JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
                              '*'
                            );
                          }
                        });
                      }}
                    />

                    {/* Custom play button overlay */}
                    {loadingVideos[video?.video_id] === false && !isPlayingMap[video?.video_id] && (
                      <Box
                        role="button"
                        tabIndex="0"
                        onClick={() => handleCustomPlay(video?.video_id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCustomPlay(video?.video_id)}
                        sx={{
                          position: 'absolute',
                          top: isMobile ? '50%' : '50%',
                          left: isMobile ? '50%' : '50%',
                          transform: 'translate(-50%, -50%)',
                          width: isMobile ? 80 : 80,
                          height: isMobile ? 54 : 50,
                          borderRadius: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'black',
                          boxShadow: '0 6px 16px rgba(0,0,0,0.35)',
                          cursor: 'pointer',
                          zIndex: 3,
                          margin: isMobile ? '-4px -4px' : '-4px -6px',
                        }}
                      >
                        <PlayArrowRoundedIcon sx={{ color: '#fff', fontSize: isMobile ? 40 : 46, marginLeft: '2px' }} />
                      </Box>
                    )}
                    {/* Overlay mask to hide YouTube title/labels inside iframe area */}
                    <Box
                      className="ameen"
                      aria-hidden
                      sx={{
                        display: isMobile && 'none',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: isMobile ? '98%' : '95%',
                        height: isMobile ? '44px' : '48px',
                        borderTopLeftRadius: 3,
                        borderTopRightRadius: 3,
                        background:
                          'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 80%, rgba(0,0,0,0) 100%)',
                        pointerEvents: 'none',
                        zIndex: 2,
                        backdropFilter: 'brightness(0)',
                      }}
                    />
                    <Box
                      className="ameen"
                      aria-hidden
                      sx={{
                        display: isMobile && 'none',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: isMobile ? '98%' : '94%',
                        height: isMobile ? '44px' : '43px',
                        borderTopLeftRadius: 3,
                        borderTopRightRadius: 3,
                        background:
                          'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 80%, rgba(0,0,0,0) 100%)',
                        pointerEvents: 'none',
                        zIndex: 2,
                        backdropFilter: 'brightness(0)',
                      }}
                    />
                    {loadingVideos[video?.video_id] !== false && (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: isMobile ? '98%' : '93%',
                          height: isMobile ? '97%' : '98%',
                          borderRadius: 3,
                          zIndex: 1,
                        }}
                      />
                    )}
                  </>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      color: '#ccc',
                      border: '1px solid #555',
                      minHeight: '172px',
                      borderRadius: '15px',
                      paddingX: 2,
                      textAlign: 'center',
                    }}
                  >
                    Invalid video ID: {video?.video_id}
                  </Box>
                )}
              </Box>
            );
          })}
        </Slider>
      </Stack>
    </div>
  );
}
